import { Checkbox } from '@ipfabric/ui-components/Checkbox/Checkbox';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import type { FrontendOnlyColumn } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { setRowSelected } from 'components/Table/model/commands/setRowSelected';
import { toggleSelectFilteredTableRows } from 'components/Table/model/commands/toggleSelectFilteredTableRows';
import { selectTableSupportsSelectAll } from 'components/Table/model/selectors/selectTableSupportsSelectAll';
import type { FC } from 'react';

const RowSelectionTitle: FC = () => {
  const ctxProps = useTableContextSelectors({
    dispatch: prop('dispatch'),
    selectState: prop('allRowsSelectState'),
    supportsSelectAll: selectTableSupportsSelectAll,
    selectedRows: prop('selectedRows'),
  });

  if (!ctxProps.supportsSelectAll) {
    return null;
  }

  return (
    // align the "select all" to the center last row of small input filter elements.
    // designed to be collapsible to minimum height if there are no filters in the header.
    <div className='flex flex-col justify-end'>
      <div className='basis-7 flex-grow-0 flex-shrink flex flex-col justify-center'>
        {ctxProps.selectState === 'pending' ? (
          <Icon glyph='fa-spinner' className='fa-spin' size='xs' />
        ) : (
          <Checkbox
            value={ctxProps.selectState === 'checked'}
            onChange={() => {
              ctxProps.dispatch(toggleSelectFilteredTableRows);
            }}
          />
        )}
      </div>
    </div>
  );
};

export const rowSelectionColumn: FrontendOnlyColumn<any> =
  createFrontendOnlyColumn({
    key: '__rowSelection',
    className: 'flex items-center',
    disabledSort: true,
    fixedWidth: true,
    sticky: true,
    title: 'Row selection',
    customFrontendTitle: <RowSelectionTitle />,
    width: 44,
    render: (row, cellContext) => {
      const rowId = cellContext.rowIdField ? row[cellContext.rowIdField] : null;

      return (
        <Checkbox
          disabled={
            cellContext.canRowBeSelected &&
            !cellContext.canRowBeSelected(
              row,
              cellContext.selectedRows
                ? {
                    selectedRows: cellContext.selectedRows,
                  }
                : undefined,
            )
          }
          value={rowId !== null && !!cellContext.selectedRows?.has(rowId)}
          onChange={(selected) => {
            cellContext.dispatch?.(setRowSelected, { rowId, selected });
          }}
        />
      );
    },
  });
